<script setup lang="ts">
import { computed, ref } from 'vue';
import { NAlert, NButton, NSpace } from 'naive-ui';
import type { ChatResumeDecision, ToolConfirmationAnswer, UserQuestionAnswer } from '@/api';
import HumanInTheLoopActionCard from '@/components/humanInTheLoop/HumanInTheLoopActionCard.vue';
import HitlChoiceList from '@/components/humanInTheLoop/HitlChoiceList.vue';
import type {
  HitlChoiceSubmission,
  HitlStatus,
  NormalizedInterrupt,
  NormalizedInterruptRequest,
} from '@/utils/humanInTheLoop/normalizeInterrupt';

export type HitlSubmitPayload =
  | { shape: 'tool_confirmation'; resumeValue: ToolConfirmationAnswer }
  | { shape: 'user_question'; resumeValue: UserQuestionAnswer }
  | { shape: 'legacy_action' | 'langgraph_action'; decisions: ChatResumeDecision[] };

const props = withDefaults(
  defineProps<{
    interrupt: NormalizedInterrupt;
    status: HitlStatus;
    errorMessage?: string | null;
    disabled?: boolean;
    /** Whether this card currently owns the digit/arrow/Enter keyboard shortcuts. */
    active?: boolean;
  }>(),
  {
    errorMessage: null,
    disabled: false,
    active: false,
  }
);

const emit = defineEmits<{ submit: [payload: HitlSubmitPayload] }>();

const submissionByRequestKey = ref<Partial<Record<string, HitlChoiceSubmission>>>({});
const finalizedRequestKeys = ref<Set<string>>(new Set());

const cardVariant = computed(() =>
  props.interrupt.shape === 'user_question' ? 'question' : 'approval'
);
const cardTitle = computed(() =>
  props.interrupt.requests.length === 1 ? props.interrupt.requests[0].title : 'Action required'
);
const cardDetails = computed(() =>
  props.interrupt.requests.length === 1 ? props.interrupt.requests[0].details : []
);
const firstUndecidedRequestKey = computed(
  () =>
    props.interrupt.requests.find(request => !finalizedRequestKeys.value.has(request.requestKey))
      ?.requestKey ?? null
);
const allRequestsDecided = computed(() =>
  props.interrupt.requests.every(request => finalizedRequestKeys.value.has(request.requestKey))
);

function finalizedLabelFor(request: NormalizedInterruptRequest): string {
  const submission = submissionByRequestKey.value[request.requestKey];
  if (!submission) return '';
  if (submission.choiceKey) {
    const choice = request.choices.find(item => item.key === submission.choiceKey);
    return choice?.label ?? submission.choiceKey;
  }
  if (submission.message) {
    const trimmed =
      submission.message.length > 60 ? `${submission.message.slice(0, 57)}...` : submission.message;
    return `Sent: "${trimmed}"`;
  }
  return 'Submitted';
}

function handleRequestConfirm(
  request: NormalizedInterruptRequest,
  submission: HitlChoiceSubmission
): void {
  submissionByRequestKey.value = {
    ...submissionByRequestKey.value,
    [request.requestKey]: submission,
  };
  finalizedRequestKeys.value = new Set([...finalizedRequestKeys.value, request.requestKey]);

  // Single-request interrupts (Shapes B/C, and most of Shape A) submit as soon
  // as their one choice list resolves. Multi-request Shape A keeps the
  // explicit "Submit decisions" button below.
  if (props.interrupt.requests.length === 1) {
    buildAndEmitSubmit();
  }
}

function buildAndEmitSubmit(): void {
  const { interrupt } = props;

  // ToolConfirmationAnswer and UserQuestionAnswer are the same `{choice?, message?}`
  // shape and both resume via `resume_value`, so one branch builds either.
  if (interrupt.shape === 'tool_confirmation' || interrupt.shape === 'user_question') {
    const submission = submissionByRequestKey.value[interrupt.requests[0].requestKey];
    if (!submission) return;
    const resumeValue: ToolConfirmationAnswer & UserQuestionAnswer = {};
    if (submission.choiceKey) resumeValue.choice = submission.choiceKey;
    if (submission.message) resumeValue.message = submission.message;
    emit('submit', { shape: interrupt.shape, resumeValue });
    return;
  }

  if (interrupt.shape === 'langgraph_action') {
    const request = interrupt.requests[0];
    const submission = submissionByRequestKey.value[request.requestKey];
    if (!submission?.choiceKey) return;
    const action = submission.choiceKey;
    const hasInput = Boolean(submission.input && Object.keys(submission.input).length > 0);
    const decisions: ChatResumeDecision[] = [
      { type: action, ...(hasInput ? { edited_action: { action, ...submission.input } } : {}) },
    ];
    emit('submit', { shape: 'langgraph_action', decisions });
    return;
  }

  const decisions: ChatResumeDecision[] = [];
  for (const request of interrupt.requests) {
    const submission = submissionByRequestKey.value[request.requestKey];
    if (!submission?.choiceKey) return;
    if (submission.choiceKey === 'approve') {
      decisions.push({ type: 'approve' });
    } else if (submission.choiceKey === 'edit') {
      decisions.push({
        type: 'edit',
        edited_action: { name: request.actionName ?? '', args: submission.editedArgs ?? {} },
      });
    } else {
      decisions.push(
        submission.message ? { type: 'reject', message: submission.message } : { type: 'reject' }
      );
    }
  }
  emit('submit', { shape: 'legacy_action', decisions });
}
</script>

<template>
  <HumanInTheLoopActionCard
    :title="cardTitle"
    :status="status"
    :details="cardDetails"
    :variant="cardVariant"
  >
    <NSpace vertical :size="16">
      <NSpace v-for="request in interrupt.requests" :key="request.requestKey" vertical :size="8">
        <div v-if="interrupt.requests.length > 1" class="hitl-request-title">
          {{ request.title }}
        </div>
        <div v-if="request.description" class="hitl-request-description">
          {{ request.description }}
        </div>

        <HitlChoiceList
          :request="request"
          :disabled="disabled || status !== 'pending'"
          :active="active && firstUndecidedRequestKey === request.requestKey"
          :finalized="finalizedRequestKeys.has(request.requestKey)"
          :finalized-label="finalizedLabelFor(request)"
          @confirm="submission => handleRequestConfirm(request, submission)"
        />
      </NSpace>
    </NSpace>

    <NButton
      v-if="interrupt.requests.length > 1"
      type="primary"
      size="small"
      style="align-self: flex-start"
      :loading="status === 'submitting'"
      :disabled="!allRequestsDecided || disabled || status !== 'pending'"
      @click="buildAndEmitSubmit"
    >
      Submit decisions
    </NButton>

    <NAlert v-if="errorMessage" type="error" :title="errorMessage" class="hitl-error-alert" />
  </HumanInTheLoopActionCard>
</template>

<style scoped>
.hitl-request-title {
  font-size: inherit;
  font-weight: 700;
}

.hitl-request-description {
  font-size: inherit;
  opacity: 0.8;
  white-space: pre-wrap;
}

.hitl-error-alert {
  margin: 0;
}
</style>
